import React, { useEffect, useRef, useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { api, getToken, assetUrl } from "../lib/api";
import { toast } from "sonner";
import { ArrowLeft, Send, Shield, AlertTriangle, MessageCircle } from "lucide-react";

// Page Messages Privés style Messenger
// Polling toutes les 3s pour le temps quasi-réel

const fmtTime = (iso) => {
  try {
    const d = new Date((iso || "").replace(" ", "T") + (iso?.endsWith("Z") ? "" : "Z"));
    const now = new Date();
    const same = d.toDateString() === now.toDateString();
    if (same) return d.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });
    return d.toLocaleDateString("fr-FR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
  } catch { return ""; }
};

const initials = (name) => (name || "M").trim().split(/\s+/).map((p) => p[0]).join("").slice(0, 2).toUpperCase();

export default function VoamadaMessagesPage() {
  const [me, setMe] = useState(null);
  const [convs, setConvs] = useState([]);
  const [active, setActive] = useState(null); // { user_id, name, picture }
  const [messages, setMessages] = useState([]);
  const [blockedByMe, setBlockedByMe] = useState(false);
  const [blockedByThem, setBlockedByThem] = useState(false);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [loadingConvs, setLoadingConvs] = useState(true);
  const [sp] = useSearchParams();
  const navigate = useNavigate();
  const msgsEnd = useRef(null);
  const pollRef = useRef(null);

  const isVoamada = typeof window !== "undefined" && /voamada\.com$/i.test(window.location.hostname);
  const backHref = isVoamada ? "/chat" : "/voamada/chat";

  // Charge le user courant
  useEffect(() => {
    if (!getToken()) { navigate("/login?next=/messages"); return; }
    api.get("/auth/me").then(({ data }) => setMe(data)).catch(() => navigate("/login"));
  }, [navigate]);

  // Charge les conversations
  const loadConvs = async () => {
    try {
      const { data } = await api.get("/voamada/dm/conversations");
      setConvs(data.conversations || []);
    } catch (e) { /* silent */ } finally { setLoadingConvs(false); }
  };
  useEffect(() => { if (me) loadConvs(); /* eslint-disable-next-line */ }, [me]);

  // Ouvre une conversation si ?with=USER_ID dans l'URL
  useEffect(() => {
    const w = sp.get("with");
    if (!w || !me) return;
    api.get(`/voamada/dm/messages?with=${encodeURIComponent(w)}`).then(({ data }) => {
      setActive(data.other);
    }).catch(() => toast.error("Utilisateur introuvable"));
  }, [sp, me]);

  // Quand une conversation est active : charge ses messages + poll
  useEffect(() => {
    if (!active || !me) return;
    let cancelled = false;
    const fetchMsgs = async (markRead = false) => {
      try {
        const { data } = await api.get(`/voamada/dm/messages?with=${encodeURIComponent(active.user_id)}`);
        if (cancelled) return;
        setMessages(data.messages || []);
        setBlockedByMe(!!data.blocked_by_me);
        setBlockedByThem(!!data.blocked_by_them);
        if (markRead) {
          api.post("/voamada/dm/mark-read", { with: active.user_id }).then(loadConvs).catch(() => {});
        }
      } catch (e) { /* silent */ }
    };
    fetchMsgs(true);
    pollRef.current = setInterval(() => fetchMsgs(false), 3000);
    return () => { cancelled = true; if (pollRef.current) clearInterval(pollRef.current); };
    // eslint-disable-next-line
  }, [active?.user_id]);

  // Auto-scroll en bas à chaque nouveau message
  useEffect(() => {
    if (msgsEnd.current) msgsEnd.current.scrollIntoView({ behavior: "smooth" });
  }, [messages.length]);

  const openConv = (other) => {
    setActive(other);
    // Marque lu visuellement immédiatement
    setConvs((cs) => cs.map((c) => c.other_id === other.user_id ? { ...c, unread: 0 } : c));
    // Update URL ?with=
    navigate(`/messages?with=${encodeURIComponent(other.user_id)}`, { replace: true });
  };

  const send = async (e) => {
    e?.preventDefault();
    if (!text.trim() || !active || sending) return;
    setSending(true);
    const body = text.trim();
    setText("");
    try {
      const { data } = await api.post("/voamada/dm/messages", { to: active.user_id, body });
      setMessages((m) => [...m, data.message]);
      loadConvs();
    } catch (err) {
      toast.error(err?.response?.data?.error || "Erreur d'envoi");
      setText(body); // restore
    } finally { setSending(false); }
  };

  const toggleBlock = async () => {
    if (!active) return;
    const next = blockedByMe ? "unblock" : "block";
    if (next === "block" && !window.confirm(`Bloquer ${active.name} ? Vous ne recevrez plus ses messages.`)) return;
    try {
      await api.post("/voamada/dm/block", { user_id: active.user_id, action: next });
      setBlockedByMe(!blockedByMe);
      toast.success(next === "block" ? "Utilisateur bloqué" : "Utilisateur débloqué");
    } catch (e) { toast.error("Erreur"); }
  };

  if (!me) return <div className="min-h-screen bg-[#0a0a0f] text-white flex items-center justify-center text-sm">Chargement…</div>;

  return (
    <div className="h-screen bg-[#0a0a0f] text-white flex flex-col" data-testid="messages-page">
      {/* Header global */}
      <header className="sticky top-0 z-30 bg-black/85 backdrop-blur-xl border-b border-yellow-400/20 px-4 md:px-8 py-3 flex items-center justify-between gap-3 shrink-0">
        <div className="flex items-center gap-3 min-w-0">
          <Link to={backHref} className="p-2 -ml-1 rounded-lg hover:bg-yellow-400/15 hover:text-yellow-400 text-white/85 transition shrink-0" data-testid="msg-back" title="Retour au chat communautaire">
            <ArrowLeft size={22} />
          </Link>
          <div className="min-w-0">
            <p className="font-black text-xl md:text-2xl tracking-tight" style={{ fontFamily: "'Anton', sans-serif" }}>
              <span className="text-yellow-400">MESSAGES</span> 💬
            </p>
            <p className="text-[10px] uppercase tracking-widest text-white/55">Conversations privées</p>
          </div>
        </div>
      </header>

      <div className="flex-1 flex min-h-0 overflow-hidden">
        {/* Sidebar conversations */}
        <aside className={`${active ? "hidden lg:flex" : "flex"} flex-col w-full lg:w-80 xl:w-96 border-r border-white/10 bg-zinc-950 shrink-0`}>
          <div className="px-4 py-4 border-b border-white/10">
            <p className="text-[10px] uppercase tracking-widest text-white/45 font-bold">Conversations</p>
          </div>
          <div className="flex-1 overflow-y-auto">
            {loadingConvs ? (
              <p className="text-center py-10 text-white/40 text-sm">Chargement…</p>
            ) : convs.length === 0 ? (
              <div className="px-5 py-10 text-center">
                <MessageCircle size={32} className="mx-auto text-white/20 mb-3" />
                <p className="text-white/55 text-sm font-bold">Aucune conversation</p>
                <p className="text-white/35 text-xs mt-2">Clique sur le pseudo d'un membre dans le chat pour lui envoyer un message privé.</p>
              </div>
            ) : convs.map((c) => (
              <button
                key={c.conv_id}
                onClick={() => openConv({ user_id: c.other_id, name: c.other_name, picture: c.other_picture })}
                className={`w-full px-4 py-3 flex items-center gap-3 border-b border-white/5 hover:bg-white/[0.04] transition text-left ${active?.user_id === c.other_id ? "bg-yellow-400/[0.06] border-l-2 border-l-yellow-400" : ""}`}
                data-testid={`conv-${c.other_id}`}
              >
                {c.other_picture ? (
                  <img src={assetUrl(c.other_picture)} alt={c.other_name} className="w-11 h-11 rounded-full object-cover shrink-0" />
                ) : (
                  <div className="w-11 h-11 rounded-full bg-yellow-400/15 text-yellow-300 flex items-center justify-center font-black text-sm shrink-0">{initials(c.other_name)}</div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2">
                    <p className="font-bold text-sm truncate">{c.other_name}</p>
                    {c.last_message_at && <span className="text-[10px] text-white/40 shrink-0">{fmtTime(c.last_message_at)}</span>}
                  </div>
                  <p className={`text-xs truncate mt-0.5 ${c.unread > 0 ? "text-white font-bold" : "text-white/45"}`}>
                    {c.last_message_preview || "Nouvelle conversation"}
                  </p>
                </div>
                {c.unread > 0 && (
                  <span className="ml-1 w-5 h-5 rounded-full bg-yellow-400 text-black text-[10px] font-black flex items-center justify-center shrink-0">{c.unread}</span>
                )}
              </button>
            ))}
          </div>
        </aside>

        {/* Panneau messages */}
        <main className={`${active ? "flex" : "hidden lg:flex"} flex-col flex-1 min-w-0`}>
          {!active ? (
            <div className="flex-1 flex items-center justify-center text-center px-6">
              <div>
                <MessageCircle size={48} className="mx-auto text-white/15 mb-4" />
                <p className="text-white/55 text-base font-bold">Choisis une conversation</p>
                <p className="text-white/35 text-sm mt-1">ou clique sur le pseudo d'un membre dans le chat.</p>
              </div>
            </div>
          ) : (
            <>
              {/* Conv header */}
              <header className="px-4 py-3 border-b border-white/10 bg-zinc-950 flex items-center gap-3 shrink-0">
                <button onClick={() => { setActive(null); navigate("/messages"); }} className="lg:hidden p-2 -ml-1 rounded-lg hover:bg-white/10" data-testid="msg-back-list">
                  <ArrowLeft size={20} />
                </button>
                {active.picture ? (
                  <img src={assetUrl(active.picture)} alt={active.name} className="w-10 h-10 rounded-full object-cover" />
                ) : (
                  <div className="w-10 h-10 rounded-full bg-yellow-400/15 text-yellow-300 flex items-center justify-center font-black text-sm">{initials(active.name)}</div>
                )}
                <div className="flex-1 min-w-0">
                  <p className="font-bold truncate">{active.name}</p>
                  {blockedByMe && <p className="text-[10px] text-red-300">Tu as bloqué cet utilisateur</p>}
                  {blockedByThem && !blockedByMe && <p className="text-[10px] text-red-300">Tu ne peux pas envoyer de message à cet utilisateur</p>}
                </div>
                <button
                  onClick={toggleBlock}
                  className="p-2 rounded-lg hover:bg-white/10 transition text-white/55 hover:text-red-300"
                  title={blockedByMe ? "Débloquer" : "Bloquer"}
                  data-testid="msg-block-toggle"
                >
                  <Shield size={17} />
                </button>
              </header>

              {/* Messages */}
              <div className="flex-1 overflow-y-auto px-4 py-5 space-y-2">
                {messages.length === 0 && (
                  <p className="text-center text-white/35 text-sm py-10">Aucun message pour le moment.<br />Envoie le premier 👋</p>
                )}
                {messages.map((m) => {
                  const mine = m.sender_id === me.user_id;
                  return (
                    <div key={m.dm_id} className={`flex ${mine ? "justify-end" : "justify-start"}`} data-testid={`dm-${m.dm_id}`}>
                      <div className={`max-w-[78%] rounded-2xl px-4 py-2.5 shadow ${mine ? "bg-yellow-400 text-black rounded-br-md" : "bg-white/[0.06] text-white rounded-bl-md"}`}>
                        <p className="whitespace-pre-wrap break-words text-sm leading-snug">{m.body}</p>
                        <p className={`text-[10px] mt-1 ${mine ? "text-black/55 text-right" : "text-white/40"}`}>
                          {fmtTime(m.created_at)} {mine && m.read_at && <span className="ml-1">✓✓</span>}
                        </p>
                      </div>
                    </div>
                  );
                })}
                <div ref={msgsEnd} />
              </div>

              {/* Composer */}
              <form onSubmit={send} className="border-t border-white/10 bg-zinc-950 px-3 py-3 flex items-end gap-2 shrink-0">
                {(blockedByMe || blockedByThem) ? (
                  <div className="flex-1 text-center py-3 text-white/55 text-sm flex items-center justify-center gap-2">
                    <AlertTriangle size={14} /> {blockedByMe ? "Débloque cet utilisateur pour envoyer un message." : "Tu ne peux pas envoyer de message."}
                  </div>
                ) : (
                  <>
                    <textarea
                      value={text}
                      onChange={(e) => setText(e.target.value)}
                      onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(e); } }}
                      placeholder="Écris ton message…"
                      rows={1}
                      maxLength={2000}
                      className="flex-1 resize-none bg-white/[0.04] border border-white/10 focus:border-yellow-400 rounded-2xl px-4 py-2.5 text-sm focus:outline-none max-h-32"
                      data-testid="dm-input"
                    />
                    <button
                      type="submit"
                      disabled={!text.trim() || sending}
                      className="w-11 h-11 rounded-full bg-yellow-400 hover:bg-yellow-300 disabled:opacity-40 text-black flex items-center justify-center transition shrink-0"
                      data-testid="dm-send-btn"
                    >
                      <Send size={17} />
                    </button>
                  </>
                )}
              </form>
            </>
          )}
        </main>
      </div>
    </div>
  );
}
